// tslint:disable:max-line-length

import { Dialog, Icon } from "armstrong-react";
import React from "react";

import * as DateUtil from "@travellocal/utils/dateHelpers";
import * as PriceUtil from "../../../bothends/priceHelpers";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import {
  AbtaLogo,
  AtolLogo,
  Link,
  LinkButton,
  PriceInCurrency,
  servicesStringToArray
} from "../../../core/atoms/atoms";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { RoutesInvoker as PortalRoutesInvoker } from "../../../portal/routesInvoker";

import "./customerPaymentSummary.scss";

interface Props {
  enquiry: IEnquiry;

  /**
   * Version of itinerary generated by LTOs
   */
  itinerarySummary?: IEnquiryItinerarySummary;

  /**
   * Version of itinerary with customer prices
   */
  itineraryQuote: IEnquiryItinerarySummary;

  isBookable?: boolean;
  lto: ILtoSummary;
  depositPaid: boolean;
  audience: UserType;
  bookingRoute?: string;
  balanceDueDays: number;
}

interface State {
  inclusionsDialogOpen?: boolean;
  exclusionsDialogOpen?: boolean;
}

interface PaymentStates {
  depositRequired: () => JSX.Element;
  fullPaymentRequired: () => JSX.Element;
  priceTbc: () => JSX.Element;
  depositPaid: () => JSX.Element;
  fullyPaid: () => JSX.Element;
}

export class CustomerPaymentSummary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  private isPriceTbc = () => this.props.itineraryQuote.totalPrice.price <= 0.001;

  private getDepositDueDate = (): IDate =>
    this.props.itineraryQuote.startDate
      ? DateUtil.subtractDays(this.props.itineraryQuote.startDate, this.props.balanceDueDays)
      : null;

  private renderHead = (s: PaymentStates) => {
    const isPaid = [EnquiryStatus.FullyPaid, EnquiryStatus.Complete, EnquiryStatus.Cancelled, EnquiryStatus.InProgress].some(x => x === this.props.enquiry.enquiryStatus);

    if (isPaid) {
      return s.fullyPaid();
    }
    else if (this.props.depositPaid) {
      return s.depositPaid();
    }
    else if (this.isPriceTbc()) {
      return s.priceTbc();
    }
    else if (DateUtil.isBefore(this.getDepositDueDate(), DateUtil.today())) {
      return s.fullPaymentRequired();
    }
    else {
      return s.depositRequired();
    }
  }

  private getAmountPaid(): IPriceInCurrency {
    const amountPaid = this.props.enquiry.totalPaid;
    return PriceUtil.subtract(this.props.itineraryQuote.totalPrice, amountPaid);
  }

  private renderPaymentButton = (label: string) => this.props.bookingRoute && (
    <div className="book-now">
      <LinkButton icomoon={Icon.Icomoon.power2} to={this.props.bookingRoute} onClick={() =>
        GoogleAnalyticsHelper.logEvent("Book now", "Click", `${window.location.pathname.includes("itinerary") ? "Itinerary" : "Payment"} tab`)}>
        {label}
      </LinkButton>

      <div className="logos cards">
        <img src="/images/credit-cards/visa.svg" height="12" width="40" />
        <img src="/images/credit-cards/mastercard.svg" height="30" width="36" />
        <img src="/images/credit-cards/maestro.svg" height="30" width="36" />
      </div>
    </div>
  )

  private renderExchangeRate = (currency: ICurrency, userType: UserType) => {
    const isAdmin = this.props.audience === UserType.Administrator;
    if (!isAdmin) {
      return null;
    }

    // Exchange rates are in reference to GBP - so GBP prices don't have exchange rates.
    if (currency.id === 1) {
      return (
        <div className="exchange-rate">
          <p className="GBP prices do not have associated exchange rates.">
            <i className="icon icon-chart" /> n/a - price is GBP
          </p>
        </div>
      );
    }

    // Exchange rates used in itinerary prices are from either the time of itinerary issue, or for booked enquiries the time of issue of the booked itinerary
    // TODO would be useful to display a note that explains at what time the rate was fixed at (i.e. either booked itinerary issue, or this itinerary issue)
    // It's not knowable without having access to all itinerary times - either DB lookup or parsing all the messages
    const exchangeRate = userType === UserType.Customer ? currency.customerExchangeRate : currency.ltoExchangeRate;
    const timestamp = currency.timestamp ? DateUtil.toMoment(currency.timestamp, true).format("DD MMM YYYY, HH:mm Z") : "unknown";

    return (
      <div className="exchange-rate">
        <p title="Exchange rate used to calculate this price">
          <i className="icon icon-chart" />
          1 GBP = {exchangeRate.toFixed(5)} {currency.code}<br />
          <small>@ {timestamp}</small><br />
        </p>
      </div>
    );
  }

  public render() {
    const { enquiry } = this.props;

    const baseItinerary = this.props.itinerarySummary;
    const quote = this.props.itineraryQuote;
    if (!quote) {
      return null;
    }

    const isCustomer = this.props.audience === UserType.Customer;
    const paymentDueDate = this.getDepositDueDate();
    const priceIsTbc = this.isPriceTbc();

    return (
      <div className="payment-grid">
        <div className="price-summary">
          <div className="sale-price-summary">
            <h4 className="version-label"><i className="icon icon-map2" />Itinerary #{quote.version}</h4>
            {!isCustomer && <h5>Customer price</h5>}

            <p className="major-price">
              {!priceIsTbc &&
                <PriceInCurrency displayForCustomer={isCustomer} priceInCurrency={quote.totalPrice} />
              }
              {priceIsTbc &&
                <span className="money-string">{quote.totalPrice.currency.symbol}TBC</span>
              }
            </p>

            {!priceIsTbc && this.renderExchangeRate(quote.totalPrice.currency, UserType.Customer)}
          </div>

          <div className="itinerary-summary">
            <div className="summary"><i className="icon icon-users" />{quote.numberOfPeople} traveller{quote.numberOfPeople > 1 && "s"}</div>
            <div className="summary"><i className="icon icon-calendar2" />{quote.days.length} day{quote.days.length > 1 && "s"}</div>
          </div>

          {this.props.isBookable && this.renderHead({
            depositRequired: () => (
              <div>
                {quote.prepayment != null &&
                  <p className="deposit-note">
                    A non-refundable deposit of <PriceInCurrency displayForCustomer={isCustomer} priceInCurrency={PriceUtil.add(quote.depositPrice, quote.prepayment.amount)} /><br />
                    is required to book. <br />
                    This amount includes: <br />
                    {quote.prepayment.reason}
                  </p>
                }
                {quote.prepayment == null &&
                  <p className="deposit-note">
                    A non-refundable deposit of <PriceInCurrency displayForCustomer={isCustomer} priceInCurrency={quote.depositPrice} /> is required to book.
                  </p>
                }
                {isCustomer &&
                  this.renderPaymentButton("Book now")
                }
              </div>
            ),
            priceTbc: () => (
              <div>
                <p className="deposit-note">This itinerary's price is still to be confirmed.</p>
              </div>
            ),
            fullPaymentRequired: () => (
              <div>
                <p className="deposit-note">Full payment is required because travel is in less than {quote.paymentTerms.balanceDueDays} days.</p>
                {isCustomer &&
                  this.renderPaymentButton("Book now")
                }
              </div>
            ),
            depositPaid: () => (
              <div>
                <p className="deposit-note">
                  The remaining balance of <PriceInCurrency displayForCustomer={isCustomer} priceInCurrency={this.getAmountPaid()} /> is due by {DateUtil.formatDate(paymentDueDate, "DD/MM/YYYY")}.<br />
                  {DateUtil.isBefore(paymentDueDate, DateUtil.today()) && "Payment is now overdue. Please pay as soon as possible."}
                </p>
                {isCustomer &&
                  this.renderPaymentButton("Make another payment")
                }
              </div>
            ),
            fullyPaid: () => <p className="deposit-note">There is nothing left to pay for this trip.</p>,
          })}

          {(!isCustomer && baseItinerary && !priceIsTbc) &&
            <div className="net-price-summary">
              <h5 className="net-price-highlight">Net price</h5>
              <p className="major-price-label"><small>(does not include commission)</small></p>
              <p className="major-price"><PriceInCurrency className="net-price-highlight" displayForCustomer={isCustomer} priceInCurrency={baseItinerary.totalPrice} /></p>

              {!priceIsTbc && this.renderExchangeRate(quote.currency, UserType.LtoEmployee)}

              <h5>Deposit amount</h5>
              <p>
                <PriceInCurrency displayForCustomer={isCustomer} priceInCurrency={PriceUtil.add(quote.depositPrice, (quote.prepayment ? quote.prepayment.amount : null))} />
              </p>

              {quote.prepayment &&
                <p>
                  An extra prepayment of <PriceInCurrency displayForCustomer={isCustomer} priceInCurrency={quote.prepayment.amount} /> was requested.
                  This is included in the total deposit amount.<br />
                  <strong>{quote.prepayment.reason}</strong>
                </p>
              }

              {!quote.prepayment && <p><em>No increased amount requested.</em></p>}
            </div>
          }
        </div>

        {((quote.lineItems && quote.lineItems.length > 0) || quote.includedServices) &&
          <div className="services-included">
            <h3>Included services</h3>
            <p className="services-disclaimer">All services are subject to availability, unless otherwise specified.</p>

            {quote.lineItems && quote.lineItems.length > 0 &&
              <table className="line-items">
                <tbody>
                  {quote.lineItems.map((lineItem, index) => (
                    <tr key={`line-item-${lineItem.id}`}>
                      <td>{lineItem.name}</td>
                      <td className="price">
                        {lineItem.unitPrice.price !== 0 &&
                          <span>
                            <PriceInCurrency className="total" displayForCustomer={isCustomer} priceInCurrency={lineItem.unitPrice} />
                            <br />
                            {(!isCustomer && baseItinerary && quote.lineItems.length === baseItinerary.lineItems.length) && // paranoia
                              <PriceInCurrency
                                className="net-price-highlight"
                                title="Net price"
                                displayForCustomer={isCustomer}
                                priceInCurrency={baseItinerary.lineItems[index].unitPrice}
                              />
                            }
                          </span>
                        }
                      </td>
                      <td>x{lineItem.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }

            {quote.includedServices &&
              <div className="services-summary">
                <Dialog title="Included services" width={900} isOpen={this.state.inclusionsDialogOpen} onClose={() => this.setState({ inclusionsDialogOpen: false })}>
                  <p style={{ whiteSpace: "pre-wrap" }}>{quote.includedServices}</p>
                </Dialog>

                <ul>
                  {servicesStringToArray(quote.includedServices)
                    .slice(0, 3)
                    .map((line, i) => <li key={`other-inc-serv-${i}`}>{line}</li>)
                  }
                </ul>
                <a className="popout-link" onClick={() => this.setState({ inclusionsDialogOpen: true })}>See all inclusions...</a>
              </div>
            }
          </div>
        }

        {quote.excludedServices &&
          <div className="services-excluded">
            <Dialog title="Excluded services" width={900} isOpen={this.state.exclusionsDialogOpen} onClose={() => this.setState({ exclusionsDialogOpen: false })}>
              <p style={{ whiteSpace: "pre-wrap" }}>{quote.excludedServices}</p>
            </Dialog>

            <h3>Excluded services</h3>
            <div className="services-summary">
              <ul>
                {servicesStringToArray(quote.excludedServices)
                  .slice(0, 3)
                  .map((line, i) => <li key={`other-inc-serv-${i}`}>{line}</li>)
                }
              </ul>
              <a className="popout-link" onClick={() => this.setState({ exclusionsDialogOpen: true })}>See all exclusions...</a>
            </div>
          </div>
        }

        {isCustomer &&
          <div className="payment-grid__terms">
            <p>
              <small>
                <a target="_blank" href={`${globalConfig.wwwBaseUrl}${WebRoutes.customerEnquiryBookingTerms(this.props.enquiry.id)}`}>{this.props.lto.name} Terms and Conditions</a>
              </small>
              <br />
              <small>
                <a target="_blank" href={`${globalConfig.wwwBaseUrl}${WebRoutes.termsOfUse()}`}>TravelLocal Terms of Use</a>
              </small>
              <br />
              <small>
                <a target="_blank" href={`${globalConfig.wwwBaseUrl}${WebRoutes.termsAndConditions()}`}>TravelLocal Terms and Conditions</a>
              </small>
              <br />
              <small>
                <a target="_blank" href={`${globalConfig.wwwBaseUrl}${WebRoutes.ltoPtr(this.props.lto.slug)}`}>Package Travel and Linked Travel Arrangements Regulations</a>
              </small>
            </p>

            <div className="payment-grid__trust">
              {enquiry.atolEligible
                ? <AtolLogo type="dark" width={100} />
                : <AbtaLogo type="colour" height={50} />
              }
            </div>
          </div>
        }

        {!isCustomer &&
          <div className="payment-grid__terms">
            <p>
              <small>
                <Link
                  to={`${globalConfig.appBaseUrl}${PortalRoutesInvoker.ltoEnquiryBookingTerms(this.props.enquiry.id)}`}>
                  {this.props.lto.name} Terms and Conditions for this enquiry
                </Link>
              </small>
            </p>
          </div>
        }

      </div>
    );
  }
}
